import React, { useMemo, useCallback } from 'react';
import {
  useGetAllBadgesQuery,
  useGetUserBadgesQuery,
  useGetBadgeSummaryQuery,
} from 'store/services/badge/badgeApi';
import { useGetBadgeProgressQuery } from 'store/services/badge/badgeApi';
import { UserBadge } from 'types/interfaces/badge';
import TrophyShelf from './TrophyShelf';
import BadgeCategorySection from './BadgeCategorySection';
import { useBadgeAutoSave } from './useBadgeAutoSave';
import { useTopRankingBadges } from './useTopRankingBadges';
import './TrophyCaseTab.css';

interface TrophyCaseTabProps {
  userId: number;
  username: string;
  isOwnProfile: boolean;
  isElite: boolean;
  onShowcaseAdded?: () => void;
}

const CATEGORY_ORDER = [
  'Sending Milestones',
  'Receiving Milestones',
  'Whale Trades',
  'Speed & Reliability',
  'Community & Social',
  'Volume Milestones',
  'Special',
  'One-at-a-Time',
];

const TrophyCaseTab: React.FC<TrophyCaseTabProps> = ({
  userId,
  username,
  isOwnProfile,
  isElite,
  onShowcaseAdded,
}) => {
  const { data: catalog, isLoading: catalogLoading } = useGetAllBadgesQuery();
  const { data: userBadges, isLoading: badgesLoading } = useGetUserBadgesQuery(userId);
  const { data: summary } = useGetBadgeSummaryQuery(userId);
  const { data: progress } = useGetBadgeProgressQuery(userId, {
    skip: !isOwnProfile,
  });

  const maxSlots = summary?.maxBadgeSlots ?? 1;

  const { toggleBadge, isSelected } = useBadgeAutoSave({
    initialDisplayed: summary?.displayedBadges,
    maxSlots,
    isOwnProfile,
  });

  const handleShowcase = useCallback((badgeId: number) => {
    const wasSelected = isSelected(badgeId);
    toggleBadge(badgeId);
    if (!wasSelected) {
      onShowcaseAdded?.();
    }
  }, [toggleBadge, isSelected, onShowcaseAdded]);

  // Build earned lookup
  const earnedMap = useMemo(() => {
    const map = new Map<number, UserBadge>();
    if (Array.isArray(userBadges)) {
      userBadges.forEach((ub) => map.set(ub.badgeId, ub));
    }
    return map;
  }, [userBadges]);

  // Compute top-ranking badges for the trophy shelf (all top badges, no limit)
  const topRankingBadges = useTopRankingBadges(userBadges);

  // Sort categories per spec order
  const sortedCategories = useMemo(() => {
    if (!catalog) return [];
    const entries = Object.entries(catalog);
    return entries.sort(([a], [b]) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [catalog]);

  const totalEarned = Array.isArray(userBadges) ? userBadges.length : 0;
  const isLoading = catalogLoading || badgesLoading;

  if (isLoading) {
    return (
      <div className="trophy-case-tab">
        <div className="trophy-case-tab__loading">Loading Trophy Case...</div>
      </div>
    );
  }

  // Empty state: no badges earned
  if (totalEarned === 0) {
    return (
      <div className="trophy-case-tab">
        <div className="trophy-case-tab__empty">
          <svg className="trophy-case-tab__empty-icon" width="64" height="64" viewBox="0 0 24 24" fill="none">
            <path d="M8 21h8m-4-4v4m-5.5-17H4a1 1 0 00-1 1v2a5 5 0 005 5h.5m2-8h5.5a1 1 0 011 1v2a5 5 0 01-5 5h-.5M7 4h10v3.5a5 5 0 01-5 5 5 5 0 01-5-5V4z" stroke="rgba(255,255,255,0.2)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <h3 className="trophy-case-tab__empty-title">No Badges Yet</h3>
          <p className="trophy-case-tab__empty-text">
            {isOwnProfile
              ? 'Start trading to earn your first badge! Badges are awarded for trading milestones and achievements.'
              : `${username} hasn't earned any badges yet. Check back later!`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="trophy-case-tab">
      {/* Hero Shelf - shows all top-ranking badges (highest per category) */}
      <TrophyShelf
        displayedBadges={topRankingBadges.map((ub) => ({
          badgeId: ub.badgeId,
          badge: ub.badge,
          earnedAt: ub.earnedAt,
        }))}
        isOwnProfile={isOwnProfile}
        isSelected={isSelected}
        onShowcase={handleShowcase}
      />

      {/* Badge Collection */}
      <div className="trophy-case-tab__collection">
        {sortedCategories.map(([category, badges], catIndex) => (
          <BadgeCategorySection
            key={category}
            category={category}
            badges={badges}
            earnedMap={earnedMap}
            progress={progress}
            isOwnProfile={isOwnProfile}
            isSelected={isSelected}
            onShowcase={handleShowcase}
            animationDelay={catIndex * 50}
          />
        ))}
      </div>
    </div>
  );
};

export default TrophyCaseTab;
